import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./ProfilePage.css";

const ProfilePage = () => {
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [labs, setLabs] = useState([]);
    const [form, setForm] = useState({
        name: "",
        email: "",
        username: "",
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        loadProfile();
    }, []);

    const loadProfile = async () => {
        try {
            const res = await fetch("/api/auth/profile", {
                credentials: "include",
            });

            if (res.status === 401) {
                navigate("/rect/login");
                return;
            }

            const data = await res.json();

            if (!res.ok)
                throw new Error(
                    data.message || "Unable to load profile."
                );

            const u = data.user || {};

            setUser(u);
            setLabs(data.labs || []);
            setForm({
                name: u.name || "",
                email: u.email || "",
                username: u.username || "",
            });
        } catch (err) {
            setError(err.message || "Unable to load profile.");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = ({ target }) => {
        setForm(prev => ({
            ...prev,
            [target.name]: target.value,
        }));
    };

    const handleSubmit = async e => {
        e.preventDefault();
        setSaving(true);
        setMessage("");
        setError("");

        try {
            const res = await fetch("/api/auth/profile", {
                method: "PUT",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    name: form.name,
                    email: form.email,
                }),
            });

            const data = await res.json();

            if (!res.ok)
                throw new Error(
                    data.message || "Unable to update profile."
                );

            const u = data.user || data;

            setUser(u);
            setForm({
                name: u.name || "",
                email: u.email || "",
                username: u.username || "",
            });

            setMessage("Profile updated successfully.");
        } catch (err) {
            setError(err.message || "Unable to update profile.");
        } finally {
            setSaving(false);
        }
    };

    if (loading)
        return (
            <main className="profile-page">
                <div className="profile-container">
                    <div className="profile-loading">
                        Loading profile...
                    </div>
                </div>
            </main>
        );

    if (!user)
        return (
            <main className="profile-page">
                <div className="profile-container">
                    <div className="profile-card">
                        <div className="profile-error">
                            {error || "Unable to load profile."}
                        </div>
                    </div>
                </div>
            </main>
        );

    const avatarLetter =
        (user.name || user.username || "U")[0].toUpperCase();

    const isGoogle = user.provider === "google";
    const isLocal = user.provider === "local";

    return (
        <main className="profile-page">
            <div className="profile-container">

                <div className="profile-header">
                    <div className="profile-avatar">
                        {avatarLetter}
                    </div>

                    <div className="profile-header-content">
                        <span className="profile-eyebrow">
                            MY ACCOUNT
                        </span>

                        <h1>
                            {user.name || user.username || "Profile"}
                        </h1>

                        <p>
                            Manage your STEMX365 account and profile.
                        </p>
                    </div>
                </div>

                <div className="profile-grid">

                    <div className="profile-main-column">

                        {/* PROFILE */}
                        <section className="profile-card">
                            <div className="profile-card-header">
                                <span className="profile-label">
                                    ACCOUNT
                                </span>
                                <h2>Profile Information</h2>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div className="profile-form-grid">

                                    <div className="profile-field profile-field-full">
                                        <label>Name</label>
                                        <input
                                            name="name"
                                            value={form.name}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="profile-field profile-field-full">
                                        <label>Username</label>
                                        <input
                                            value={form.username}
                                            disabled
                                        />
                                        <small>
                                            Username cannot be changed.
                                        </small>
                                    </div>

                                    <div className="profile-field profile-field-full">
                                        <label>Email Address</label>
                                        <input
                                            name="email"
                                            type="email"
                                            value={form.email}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>

                                </div>

                                {message && (
                                    <div className="profile-message">
                                        {message}
                                    </div>
                                )}

                                {error && (
                                    <div className="profile-error">
                                        {error}
                                    </div>
                                )}

                                <div className="profile-actions">
                                    <button
                                        type="submit"
                                        className="profile-save-btn"
                                        disabled={saving}
                                    >
                                        {saving
                                            ? "Saving..."
                                            : "Save Changes"}
                                    </button>
                                </div>
                            </form>
                        </section>


                        {/* LABS */}
                        <section className="profile-card profile-labs-card">
                            <div className="profile-card-header">
                                <span className="profile-label">
                                    MY LABS
                                </span>

                                <h2>Labs You Can Use</h2>

                                <p>
                                    STEMX365 labs currently available
                                    to your account.
                                </p>
                            </div>

                            {labs.length ? (
                                <div className="profile-labs-grid">

                                    {labs.map(lab => (
                                        <div
                                            className="profile-lab"
                                            key={lab.id}
                                        >

                                            {lab.image_url ? (
                                                <img
                                                    src={lab.image_url}
                                                    alt={lab.name}
                                                    className="profile-lab-image"
                                                />
                                            ) : (
                                                <div className="profile-lab-image profile-lab-placeholder">
                                                    LAB
                                                </div>
                                            )}

                                            <div className="profile-lab-content">

                                                <h3>{lab.name}</h3>

                                                {lab.description && (
                                                    <p>
                                                        {lab.description}
                                                    </p>
                                                )}

                                                {lab.subscription && (
                                                    <div className="profile-lab-subscription">
                                                        <span>
                                                            {lab.subscription.plan}
                                                        </span>
                                                        <strong>
                                                            {lab.subscription.status}
                                                        </strong>
                                                    </div>
                                                )}

                                                <div className="profile-lab-footer">

                                                    <span className="profile-lab-status">
                                                        {lab.subscribed
                                                            ? "Active"
                                                            : "Not Subscribed"}
                                                    </span>

                                                    <button
                                                        type="button"
                                                        className="profile-lab-btn"
                                                        onClick={() =>
                                                            lab.subscribed
                                                                ? navigate(
                                                                    `/rect/app/projects/${lab.id}`
                                                                )
                                                                : navigate(
                                                                    `/rect/app/labs/pricing/${lab.id}`
                                                                )
                                                        }
                                                    >
                                                        {lab.subscribed
                                                            ? "Launch Lab"
                                                            : "Subscribe"}
                                                    </button>

                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                </div>
                            ) : (
                                <div className="profile-no-labs">
                                    <h3>No Labs Available</h3>
                                    <p>
                                        Your account currently does not
                                        have access to any labs.
                                    </p>
                                </div>
                            )}
                        </section>

                    </div>


                    {/* SIDEBAR */}
                    <aside className="profile-sidebar">

                        <section className="profile-card">
                            <span className="profile-label">
                                ACCOUNT STATUS
                            </span>

                            <div className="profile-status">
                                <span className="profile-status-dot" />
                                <strong>
                                    {user.subscription_status || "Active"}
                                </strong>
                            </div>

                            <div className="profile-info-list">

                                <div>
                                    <span>Role</span>
                                    <strong>
                                        {user.role || "Student"}
                                    </strong>
                                </div>

                                <div>
                                    <span>Account</span>
                                    <strong>
                                        {user.admin_approved
                                            ? "Approved"
                                            : "Active"}
                                    </strong>
                                </div>

                                <div>
                                    <span>Labs</span>
                                    <strong>{labs.length}</strong>
                                </div>

                                <div>
                                    <span>Login</span>
                                    <strong>
                                        {isGoogle
                                            ? "Google"
                                            : "STEMX365"}
                                    </strong>
                                </div>

                            </div>
                        </section>


                        {/* LOCAL ACCOUNT */}
                        {isLocal && (
                            <section className="profile-card">
                                <span className="profile-label">
                                    SECURITY
                                </span>

                                <h3>Password & Security</h3>

                                <p>
                                    Update your STEMX365 account password.
                                </p>

                                <button
                                    type="button"
                                    className="profile-secondary-btn"
                                    onClick={() =>
                                        navigate(
                                            "/rect/change-password"
                                        )
                                    }
                                >
                                    Change Password
                                </button>
                            </section>
                        )}


                        {/* GOOGLE ACCOUNT */}
                        {isGoogle && (
                            <section className="profile-card">
                                <span className="profile-label">
                                    SECURITY
                                </span>

                                <h3>Account Security</h3>

                                <p>
                                    Your account is secured through Google.
                                    Password management is handled by Google.
                                </p>
                            </section>
                        )}

                    </aside>

                </div>
            </div>
        </main>
    );
};

export default ProfilePage;